/**
 * Generador de IDs únicos para nodos y entidades
 * @module utils/idGenerator
 */

import { NodeType } from '@/types/nodes'
import { Carpeta } from '@/types/folders'
import { ID_PREFIXES } from '@/constants'

/**
 * Genera un ID único basado en timestamp
 */
export function generateUniqueId(prefix: string): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 7)
  return `${prefix}-${timestamp}${random}`.toUpperCase()
}

/**
 * Obtiene el prefijo correcto para un tipo de nodo
 */
export function getNodePrefix(nodeType: NodeType): string {
  const prefixMap: Record<NodeType, string> = {
    OLT: ID_PREFIXES.OLT,
    POP: ID_PREFIXES.POP,
    NAP: ID_PREFIXES.NAP,
    CIERRE: ID_PREFIXES.CIERRE,
    CLIENTE: ID_PREFIXES.CLIENTE,
    POSTE: ID_PREFIXES.POSTE,
  }

  return prefixMap[nodeType]
}

/**
 * Obtiene la ruta completa de carpetas desde raíz
 */
export function getFolderPath(
  carpeta: Carpeta,
  allFolders: Map<string, Carpeta>
): Carpeta[] {
  const path: Carpeta[] = []
  let current: Carpeta | undefined = carpeta

  while (current) {
    path.unshift(current)
    current = current.padreId ? allFolders.get(current.padreId) : undefined
  }

  return path
}

/**
 * Genera un ID jerárquico basado en la estructura de carpetas
 *
 * @example
 * // Red Principal (RP) → Zona Norte (ZN) → Sector A (SA) → NAP
 * generateHierarchicalId(carpetaSectorA, 'NAP', allFolders, existingNodes)
 * // "RP-ZN-SA-NAP-001"
 */
export function generateHierarchicalId(
  carpeta: Carpeta,
  nodeType: NodeType,
  allFolders: Map<string, Carpeta>,
  existingNodeIds: string[]
): string {
  // 1. Obtener ruta completa de carpetas
  const path = getFolderPath(carpeta, allFolders)

  // 2. Extraer identificadores de cada carpeta
  const prefixes = path.map((folder) => folder.identificador)

  // 3. Agregar prefijo del tipo de nodo
  const nodePrefix = getNodePrefix(nodeType)

  // 4. Generar número consecutivo
  const number = getNextNumber(prefixes, nodePrefix, existingNodeIds)

  // 5. Construir ID completo
  const hierarchicalId = [...prefixes, nodePrefix, number].join('-')

  return hierarchicalId
}

/**
 * Obtiene el siguiente número consecutivo disponible
 */
function getNextNumber(
  prefixes: string[],
  nodePrefix: string,
  existingNodeIds: string[]
): string {
  // Patrón: RP-ZN-SA-NAP-XXX
  const pattern = [...prefixes, nodePrefix].join('-')

  // Filtrar solo los IDs que coincidan con este patrón
  const matchingIds = existingNodeIds.filter((id) => id.startsWith(pattern))

  // Extraer los números
  const numbers = matchingIds
    .map((id) => {
      const parts = id.split('-')
      const lastPart = parts[parts.length - 1]
      return parseInt(lastPart, 10)
    })
    .filter((n) => !isNaN(n))

  // Obtener el máximo y sumar 1
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0
  const nextNumber = maxNumber + 1

  // Retornar con padding de 3 dígitos
  return nextNumber.toString().padStart(3, '0')
}

/**
 * Genera ID para una carpeta
 */
export function generateFolderId(): string {
  return generateUniqueId(ID_PREFIXES.FOLDER)
}

/**
 * Genera ID para un proyecto
 */
export function generateProjectId(): string {
  return generateUniqueId(ID_PREFIXES.PROJECT)
}

/**
 * Genera ID para un cable
 */
export function generateCableId(): string {
  return generateUniqueId(ID_PREFIXES.CABLE)
}

/**
 * Extrae el tipo de nodo desde un ID
 */
export function extractNodeTypeFromId(id: string): NodeType | null {
  const parts = id.split('-')

  // Buscar el prefijo del nodo (debe estar antes del número final)
  const typeIndex = parts.length >= 2 ? parts.length - 2 : -1

  if (typeIndex < 0) return null

  const typePrefix = parts[typeIndex]

  const typeMap: Record<string, NodeType> = {
    OLT: 'OLT',
    POP: 'POP',
    NAP: 'NAP',
    CIE: 'CIERRE',
    CLI: 'CLIENTE',
    POS: 'POSTE',
  }

  return typeMap[typePrefix] || null
}

/**
 * Valida que un ID tenga el formato correcto
 */
export function isValidNodeId(id: string): boolean {
  // Debe contener al menos: PREFIJO-TIPO-NUMERO
  // Ejemplo: RP-NAP-001 o RP-ZN-SA-NAP-001
  const parts = id.split('-')

  if (parts.length < 3) return false

  // El último debe ser un número
  const lastPart = parts[parts.length - 1]
  if (!/^\d{3}$/.test(lastPart)) return false

  // El penúltimo debe ser un prefijo de tipo válido
  const typePart = parts[parts.length - 2]
  const validTypePrefixes = ['OLT', 'POP', 'NAP', 'CIE', 'CLI', 'POS']

  return validTypePrefixes.includes(typePart)
}
